/**
 * Cookie 工具类
 * 提供获取、设置和删除 Cookie 的方法
 */

/**
 * Cookie 选项接口
 */
export interface CookieOptions {
  /** 过期时间（单位：天） */
  expires?: number;
  /** 路径 */
  path?: string;
  /** 域名 */
  domain?: string;
  /** 是否仅在 HTTPS 下传输 */
  secure?: boolean;
  /** SameSite 属性，可选值：strict、lax、none */
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * 根据名称获取 Cookie 值
 * @param name Cookie 名称
 * @returns Cookie 值，如果不存在则返回 undefined
 */
export function getCookie(name: string): string | undefined {
  // SSR兼容性：服务端渲染时document不存在，返回undefined
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookieName = `${encodeURIComponent(name)}=`;
  const cookieArray = document.cookie.split(';');

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i].trim();
    if (cookie.indexOf(cookieName) === 0) {
      return decodeURIComponent(cookie.substring(cookieName.length, cookie.length));
    }
  }

  return undefined;
}

/**
 * 设置 Cookie
 * @param name Cookie 名称
 * @param value Cookie 值
 * @param options Cookie 选项
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  // SSR兼容性：服务端渲染时document不存在，跳过设置
  if (typeof document === 'undefined') {
    console.warn('[cookie] Document not available, skipping setCookie');
    return;
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // 处理过期时间
  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + (options.expires * 24 * 60 * 60 * 1000));
    cookieString += `; expires=${date.toUTCString()}`;
  }

  // 处理路径
  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  // 处理域名
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  // 处理 secure
  if (options.secure) {
    cookieString += '; secure';
  }

  // 处理 SameSite
  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * 删除 Cookie
 * @param name Cookie 名称
 * @param options Cookie 选项（需要与设置时的选项匹配）
 */
export function deleteCookie(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void {
  // 设置过期时间为过去，从而删除 Cookie
  setCookie(name, '', { ...options, expires: -1 });
}

// ============================================
// Cookie 多 key 批量取值（客户端 & 服务端）
// ============================================

/**
 * 从请求头 cookie 字符串中解析单个 key 的值（支持 value 中的 =，并做 decodeURIComponent）
 */
export function getCookieValueFromHeader(
  cookieHeader: string,
  key: string,
): string | undefined {
  for (const part of cookieHeader.split(";")) {
    const [rawK, ...rawVParts] = part.trim().split("=");
    if (rawK?.trim() !== key) continue;
    const rawV = rawVParts.join("=").trim();
    try {
      return decodeURIComponent(rawV);
    } catch {
      return rawV;
    }
  }
  return undefined;
}

/**
 * 从请求头 cookie 字符串中按 keys 顺序取第一个非空值
 */
export function getFirstCookieFromHeader(
  cookieHeader: string | null | undefined,
  keys: readonly string[],
): string | undefined {
  if (!cookieHeader) return undefined;
  for (const key of keys) {
    const v = getCookieValueFromHeader(cookieHeader, key)?.trim();
    if (v) return v;
  }
  return undefined;
}

/**
 * 客户端：从 document.cookie 按 keys 顺序取第一个非空值
 */
export function getFirstCookieClient(keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const v = getCookie(key)?.trim();
    if (v) return v;
  }
  return undefined;
}
